import ListHeader from "@/components/design-system/list-header";
import ListPagination from "@/components/design-system/list-pagination";
import {
  Table,
  TableSortConfig,
  type TableColumn,
} from "@/components/design-system/table";
import {
  deleteExistingStudent,
  fetchStudents,
  selectStudentActionLoading,
  selectStudentLoading,
  selectStudents,
  selectTotalItems,
} from "@/store/slices/studentSlice";
import { AppDispatch } from "@store";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

// Using the StudentModel from types
import { Button } from "@/components/design-system/button";
import ExportDropdown, {
  ExportOption,
} from "@/components/design-system/export-dropdown";
import Modal from "@/components/design-system/Modal";
import { downloadStudentService } from "@/service/student.service";
import { selectUserRole } from "@/store/slices/authSlice";
import { StudentModel, StudentQuery, type StudentListType } from "@/types/student.model";
import {
  CSVIcon,
  downloadFileFromBlob,
  evaluationColumns,
  ExcelIcon,
  FileFormats,
  hasMenuPermission,
  hasRolePermissions,
  PDFIcon,
  ROLE_PERMISSIONS,
  successToast,
} from "@/utils/constants";
import { Eye } from "lucide-react";
import { setNavigationSource } from "@/store/slices/courseSlice";
import {
  fetchCsListDropdownOptions,
  type CsListGradeOption,
} from "@/service/base.service";
import SchoolSelect from "@/components/design-system/schoolSelect";
import StudentTypeSelect from "@/components/design-system/StudentTypeSelect";
import StudentTypeBadge, {
  resolveStudentType,
} from "@/modules/User-Management/components/StudentTypeBadge";
import { getStudentCourseViewNavigation, resolveStudentClassName } from "@/utils/studentViewNavigation";
import { toStudentListResourceParams } from "@/utils/studentListResourceParams";

// Extended interface for UI display purposes
interface StudentDisplay extends StudentModel {
  sno?: number;
  school?: {
    name: string;
  };
}

const ITEMS_PER_PAGE = 10; // Or any other value you prefer

export default function TeacherDashboardStudentListSection({
  teacherId,
}: {
  teacherId: string;
}) {
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [sortConfig, setSortConfig] = useState<TableSortConfig | undefined>(
    undefined
  );

  const [selectedSchool, setSelectedSchool] = useState<string | undefined>(
    undefined
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<string | null>(null);

  const [gradeOptions, setGradeOptions] = useState<CsListGradeOption[]>([]);
  const [selectedGradeId, setSelectedGradeId] = useState<string>("");
  const [selectedListType, setSelectedListType] = useState<StudentListType>("all");

  const dispatch = useDispatch<AppDispatch>();
  const students = useSelector(selectStudents);
  const actionLoading = useSelector(selectStudentActionLoading);
  const loading = useSelector(selectStudentLoading);
  const totalItems = useSelector(selectTotalItems);
  const userRole = useSelector(selectUserRole);

  const navigate = useNavigate();

  // Map column keys to API sort keys
  const sortKeyMap: Record<string, string> = {
    name: "NME",
    reg_no: "REG",
    email: "EML",
    grade: "GRDE",
    course: "CSE",
    age: "AGE",
    "school.name": "SCH",
  };

  // Handle sorting
  const handleSort = (newSortConfig: TableSortConfig | null) => {
    // Convert null to undefined for compatibility with the Table component
    setSortConfig(newSortConfig || undefined);
    // Reset to first page when sort changes
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Track if filters (like grade options) are ready to fetch
  const hasFiltersReady = useRef(false);

  // Course / Class / All options from v2 dp/cs/list?type=…
  const fetchGradeOptions = async () => {
    try {
      const options = await fetchCsListDropdownOptions(selectedListType);
      setGradeOptions(options);
      hasFiltersReady.current = true;
    } catch (error) {
      console.error("Error fetching grade options:", error);
      setGradeOptions([]);
      hasFiltersReady.current = true;
    }
  };

  useEffect(() => {
    fetchGradeOptions();
  }, [teacherId, selectedListType]);

  useEffect(() => {
    if (gradeOptions.length > 0) {
      setSelectedGradeId(gradeOptions?.[0]?.id);
    } else {
      setSelectedGradeId("");
    }
  }, [gradeOptions]);

  // Handle grade selection change
  const handleGradeChange = (gradeIdSelected: string) => {
    setSelectedGradeId(gradeIdSelected);
    // Reset to first page when grade filter changes
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleListTypeChange = (type: StudentListType) => {
    setSelectedListType(type);
    setSelectedGradeId("");
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const columns: TableColumn<StudentDisplay>[] = [
    {
      key: "sno",
      title: "S.No",
      dataIndex: "sno",
      minWidth: 68,
      maxWidth: 68,
      align: "center",
      render: (_, __, index) => {
        // Calculate S.No based on current page and index
        const sno = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
        return (
          <div className="text-[#1F1F1F] text-[16px] font-medium text-center text-borderBlack">
            {String(sno).padStart(2, "0")}
          </div>
        );
      },
    },
    {
      key: "reg_no",
      title: "Unique ID",
      dataIndex: "reg_no",
      minWidth: 145,
      maxWidth: 145,
      sortable: true,
      sortKey: "reg_no",
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {record.reg_no}
        </div>
      ),
    },
    {
      key: "name",
      title: "Student Name",
      dataIndex: "name",
      minWidth: 250,
      maxWidth: 250,
      sortable: true,
      sortKey: "name",
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
            lineHeight: 1.25,
          }}
        >
          {record.name}
        </div>
      ),
    },
    {
      key: "type",
      title: "Type",
      minWidth: 120,
      maxWidth: 120,
      align: "center",
      render: (_, record) => (
        <div className="flex justify-center">
          <StudentTypeBadge type={resolveStudentType(record)} />
        </div>
      ),
    },
    ...(selectedListType !== "course"
      ? [
          {
            key: "class_name",
            title: "Class Name",
            minWidth: 180,
            maxWidth: 220,
            render: (_: unknown, record: StudentDisplay) => (
              <div
                className="text-[#1F1F1F] text-[16px] font-medium"
                style={{
                  overflowWrap: "anywhere",
                  whiteSpace: "pre-wrap",
                }}
              >
                {resolveStudentClassName(record) || "—"}
              </div>
            ),
          } satisfies TableColumn<StudentDisplay>,
        ]
      : []),
    {
      key: "grade",
      title: "Grade",
      minWidth: 128,
      maxWidth: 128,
      sortable: true,
      sortKey: "grade",
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
            lineHeight: 1.25,
          }}
        >
          {Array.isArray(record.grade)
            ? record.grade
              .map((grade) =>
                typeof grade === "object" && grade !== null
                  ? grade.grade
                  : grade
              )
              .join(", ")
            : typeof record.grade === "object" && record.grade !== null
              ? record.grade.grade
              : record.grade || "N/A"}
        </div>
      ),
    },
    {
      key: "age",
      title: "Age",
      minWidth: 128,
      maxWidth: 128,
      sortable: true,
      sortKey: "age",
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {record.age || "16"}
        </div>
      ),
    },
    {
      key: "email",
      title: "Email Address",
      minWidth: 249,
      maxWidth: 249,
      sortable: true,
      sortKey: "email",
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
            lineHeight: 1.25,
          }}
        >
          {record.email || "N/A"}
        </div>
      ),
    },
    {
      key: "percentage",
      title: "Course Completion %",
      minWidth: 220,
      maxWidth: 220,
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {record.course_completion || "0%"}
        </div>
      ),
    },
    ...evaluationColumns(selectedGradeId)
    // {
    //   key: "teacher",
    //   title: "Teacher Assigned",
    //   minWidth: 204,
    //   maxWidth: 204,
    //   render: (_, record) => (
    //     <div
    //       className="text-[#1F1F1F] text-[16px] font-medium"
    //       style={{
    //         overflowWrap: "anywhere",
    //         whiteSpace: "pre-wrap",
    //       }}
    //     >
    //       {Array.isArray(record.teachers)
    //         ? record.teachers.map((teacher) => teacher.name).join(", ")
    //         : record.teachers || "N/A"}
    //     </div>
    //   ),
    // },
  ];

  if (
    hasRolePermissions(userRole?.permissions as string[], [
      ROLE_PERMISSIONS.ACTION_VIEW_STUDENT,
      ROLE_PERMISSIONS.ACTION_EDIT_STUDENT,
      ROLE_PERMISSIONS.ACTION_DELETE_STUDENT,
    ])
  ) {
    columns.push({
      key: "action",
      title: "Action",
      minWidth: 175,
      maxWidth: 175,
      align: "left",
      fixed: "right",
      render: (_, record) => (
        <div className="flex items-center justify-start space-x-2">
          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_VIEW_STUDENT,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="View"
                onClick={() => {
                  const target = getStudentCourseViewNavigation(record);
                  if (!target) return;

                  dispatch(setNavigationSource("dashboard"));
                  navigate(target.path, { state: target.state });
                }}
              >
                <Eye className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}
        </div>
      ),
    });
  }

  // Handle delete student
  const handleDeleteStudent = () => {
    if (studentToDelete) {
      dispatch(deleteExistingStudent(studentToDelete))
        .unwrap()
        .then(() => {
          setDeleteModalOpen(false);
          setStudentToDelete(null);
        })
        .catch((error) => {
          console.log("Failed to delete Student:", error);
          // Modal stays open on error
        });
    }
  };

  const handleDateRangeChange = (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => {
    setFromDate(startDate);
    setToDate(endDate);
    // Reset to first page when date range changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      // The page change will trigger a fetch
    }
  };

  // Track if component is mounted
  const isMounted = useRef(false);
  // Track if we need to fetch data
  const shouldFetch = useRef(true);

  // Function to fetch students data
  const fetchStudentsData = () => {
    // Prepare sort parameter if sortConfig exists
    let sortBy: string | undefined = undefined;
    if (sortConfig) {
      const apiSortKey = sortKeyMap[sortConfig.key];
      if (apiSortKey) {
        sortBy = `${apiSortKey}`;
      }
    }

    // Only fetch if we should
    if (shouldFetch.current && teacherId) {
      shouldFetch.current = false; // Prevent additional fetches until explicitly allowed

      const selectedOption = gradeOptions.find((o) => o.id === selectedGradeId);
      dispatch(
        fetchStudents({
          page: currentPage,
          pageSize: ITEMS_PER_PAGE,
          search: searchValue,
          fromDate: fromDate,
          toDate: toDate,
          sortBy: sortBy,
          teacherId: teacherId || "",
          type: selectedListType,
          ...toStudentListResourceParams(
            selectedListType,
            selectedGradeId,
            selectedOption?.kind
          ),
        })
      );
    }
  };

  // Handle search value changes
  useEffect(() => {
    if (!isMounted.current) return;

    // Reset to first page when search value changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      // Don't fetch here, let the page change trigger it
    } else {
      // Allow fetching
      shouldFetch.current = true;
      fetchStudentsData();
    }
  }, [searchValue, teacherId]);

  // Handle school selection changes
  useEffect(() => {
    if (!isMounted.current) return;

    // Reset to first page when school filter changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      // Don't fetch here, let the page change trigger it
    } else {
      // Allow fetching
      shouldFetch.current = true;
      fetchStudentsData();
    }
  }, [selectedSchool, selectedGradeId, selectedListType]);

  // Handle date range changes
  useEffect(() => {
    if (!isMounted.current) return;

    // Reset to first page when date range changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      // Don't fetch here, let the page change trigger it
    } else {
      // Allow fetching
      shouldFetch.current = true;
      fetchStudentsData();
    }
  }, [fromDate, toDate]);

  // Handle sort changes
  useEffect(() => {
    if (!isMounted.current) return;

    // Allow fetching
    shouldFetch.current = true;
    fetchStudentsData();
  }, [sortConfig]);

  // Handle page changes
  useEffect(() => {
    if (!isMounted.current) return;

    // Allow fetching
    shouldFetch.current = true;
    fetchStudentsData();
  }, [currentPage]);

  // Initial fetch on component mount
  useEffect(() => {
    isMounted.current = true;
    fetchStudentsData();

    return () => {
      isMounted.current = false;
    };
  }, []);

  const exportOptions: ExportOption[] = [
    {
      label: "PDF",
      icon: <PDFIcon />,
      action: () => handleExport("pdf"),
    },
    {
      label: "Excel",
      icon: <ExcelIcon />,
      action: () => handleExport("xl"),
    },
    {
      label: "CSV",
      icon: <CSVIcon />,
      action: () => handleExport("csv"),
    },
  ];
  // Handle export of student data as Excel, PDF or CSV
  const handleExport = async (type: "xl" | "pdf" | "csv") => {
    try {
      // Create query params from current filters
      const selectedOption = gradeOptions.find((o) => o.id === selectedGradeId);
      const queryParams: StudentQuery = {
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        search: searchValue || undefined,
        fromDate: fromDate,
        toDate: toDate,
        teacherId: teacherId,
        schoolId: selectedSchool || undefined,
        type: selectedListType,
        ...toStudentListResourceParams(
          selectedListType,
          selectedGradeId,
          selectedOption?.kind
        ),
      };
      const response = await downloadStudentService(type, queryParams);
      downloadFileFromBlob(response, type, "student_report");
      successToast(
        "Export Successful",
        `Students exported as ${FileFormats[type].name} successfully`
      );
    } catch (error) {
      console.error(`Error exporting students as ${type}:`, error);
    }
  };

  return (
    <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden h-full flex flex-1 flex-col">
      <ListHeader
        title="Students List"
        titleClassName="w-auto h-[27px]"
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onDateRangeChange={handleDateRangeChange}
        rightAction={
          <>
            {hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_EXPORT_STUDENT,
              userRole?.permissions as string[]
            ) && <ExportDropdown options={exportOptions} />}
          </>
        }
        leftAction={
          <>
            <StudentTypeSelect
              value={selectedListType}
              onChange={handleListTypeChange}
              className="min-w-[200px] w-full shrink-0 sm:w-[200px]"
            />
            <div className="min-w-[200px] w-full shrink-0 sm:w-[320px]">
              <SchoolSelect
                label=""
                placeholder={
                  selectedListType === "class"
                    ? "Select Class"
                    : selectedListType === "course"
                      ? "Select Course"
                      : "Select Course or Class"
                }
                options={gradeOptions.map((g) => ({ value: g.id, label: g.grade }))}
                value={selectedGradeId}
                hideCloseIcon
                onChange={handleGradeChange}
                showAbove={false}
                maxHeight="400px"
              />
            </div>
          </>
        }
      />

      {/* Table Container with fixed header */}
      <div className="flex flex-1 flex-col border border-[#EDEDED] rounded-md overflow-x-auto">
        <div className="flex-1 overflow-auto h-full flex">
          <Table
            columns={columns}
            data={students}
            rowKey="id"
            showHeader={true}
            size="middle"
            sticky
            scroll={{ x: 1200 }}
            loading={loading}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        </div>
      </div>

      <ListPagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalItems={totalItems}
        itemsPerPage={ITEMS_PER_PAGE}
        itemsLabel="Students"
      />

      {/* Delete Student Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Student"
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] mb-8">
            Are you sure want to Delete Student ?
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() => setDeleteModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={() => handleDeleteStudent()}
            disabled={actionLoading}
          >
            {actionLoading ? "Deleting..." : "Yes, Delete"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
